import { useCallback, useEffect, useState } from "react"

import { getAIChatQuery } from "@/api"
import type { AIConversationMessage } from "@/api/others/ai"
import { sendToBD } from "@/api/others/ai"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import type { AIMessage } from "@/modules/types"

const DAILY_REQUEST_LIMIT = 5
const STORAGE_KEY_DAILY_REQUESTS = "daily_ai_requests"

export interface UseAIChatReturn {
  messages: AIMessage[]
  isLoading: boolean
  error: string | null
  dailyRequestCount: number
  dailyLimit: number
  sendMessage: (message: string, context?: string, options?: { isRetry?: boolean }) => Promise<void>
  clearMessages: () => void
  setMessages: React.Dispatch<React.SetStateAction<AIMessage[]>>
  retryMessage: (originalMessage: string) => Promise<void>
}

export const useAIChat = (initialMessages: AIMessage[] = []): UseAIChatReturn => {
  const [messages, setMessages] = useState<AIMessage[]>(initialMessages)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dailyRequestCount, setDailyRequestCount] = useState(0)
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().split("T")[0])
  const { sendMessage: sendMessageToAI, saveChatMessage } = getAIChatQuery()
  const { user } = useCurrentUser()

  // Update messages when initialMessages changes (after API load)
  // Ensure messages are always in chronological order (oldest first)
  useEffect(() => {
    if (initialMessages.length > 0) {
      // Sort messages by timestamp to ensure chronological order
      const sortedMessages = [...initialMessages].sort(
        (a, b) => (a.timestamp || 0) - (b.timestamp || 0)
      )
      setMessages(sortedMessages)
    }
  }, [initialMessages])

  // Load daily requests from localStorage
  const loadDailyRequests = useCallback(async () => {
    try {
      const storedData = localStorage.getItem(STORAGE_KEY_DAILY_REQUESTS)
      if (storedData) {
        const { date, count } = JSON.parse(storedData)
        const today = new Date().toISOString().split("T")[0]

        if (date === today) {
          setDailyRequestCount(count)
          setCurrentDate(date)
        } else {
          // New day, reset counter
          setDailyRequestCount(0)
          setCurrentDate(today)
          localStorage.setItem(
            STORAGE_KEY_DAILY_REQUESTS,
            JSON.stringify({ date: today, count: 0 })
          )
        }
      } else {
        // First time, initialize
        const today = new Date().toISOString().split("T")[0]
        setCurrentDate(today)
        localStorage.setItem(STORAGE_KEY_DAILY_REQUESTS, JSON.stringify({ date: today, count: 0 }))
      }
    } catch (error) {
      console.error("Error loading daily requests:", error)
    }
  }, [])

  const incrementDailyRequests = useCallback(async () => {
    try {
      const newCount = dailyRequestCount + 1
      setDailyRequestCount(newCount)
      localStorage.setItem(
        STORAGE_KEY_DAILY_REQUESTS,
        JSON.stringify({ date: currentDate, count: newCount })
      )
    } catch (error) {
      console.error("Error incrementing daily requests:", error)
    }
  }, [dailyRequestCount, currentDate])

  // Load daily requests on component mount
  useEffect(() => {
    loadDailyRequests()
  }, [loadDailyRequests])

  // Get conversation history for context
  const buildConversationContext = useCallback(
    (currentMessages: AIMessage[]): AIConversationMessage[] | undefined => {
      const validMessages = currentMessages.filter((msg) => !msg.isTyping)
      const recentMessages = validMessages.slice(-6)

      if (recentMessages.length === 0) return undefined

      const history = recentMessages
        .filter((msg) => msg.content?.trim())
        .map((msg) => ({
          content: msg.content!,
          role: (msg.isAI ? "assistant" : "user") as "user" | "assistant"
        }))

      return history.length > 0 ? history : undefined
    },
    []
  )

  // Simulate AI response for fallback

  const sendMessage = useCallback(
    async (message: string, context?: string, options?: { isRetry?: boolean }) => {
      if (!message.trim() || isLoading) return

      // Check daily request limit
      if (dailyRequestCount >= DAILY_REQUEST_LIMIT) {
        setError(
          `Bạn đã sử dụng hết ${DAILY_REQUEST_LIMIT} câu hỏi trong ngày hôm nay. Vui lòng thử lại vào ngày mai.`
        )
        return
      }

      // Add user message (append to end - chronological order)
      const userMessage: AIMessage = {
        id: Date.now().toString(),
        content: message,
        isAI: false,
        timestamp: Date.now(),
        createdAt: new Date().toISOString()
      }

      setMessages((prev) => [...prev, userMessage])

      // Save user message to database (async, non-blocking)
      try {
        saveChatMessage(message.trim(), "KOC").catch((error) => {
          console.error("Failed to save user message:", error)
        })
      } catch (error) {
        console.error("Error saving user message:", error)
      }

      // Add typing indicator
      const typingMessage: AIMessage = {
        id: `typing-${Date.now()}`,
        content: "",
        isAI: true,
        isTyping: true,
        timestamp: Date.now(),
        createdAt: new Date().toISOString()
      }
      setMessages((prev) => [...prev, typingMessage])

      setIsLoading(true)
      setError(null)

      // Increment daily request counter (skip when retry)
      if (!options?.isRetry) {
        await incrementDailyRequests()
      }

      try {
        // Get conversation history
        const history = buildConversationContext(messages)
        const conversationHistory: AIConversationMessage[] | undefined = context
          ? [{ content: context, role: "user" }]
          : history

        let response: any
        try {
          response = await sendMessageToAI(
            message,
            conversationHistory,
            user?.id ? parseInt(user.id) : undefined
          )
        } catch (apiError) {
          console.log("apiError :>> ", apiError)
        }

        // Remove typing indicator
        setMessages((prev) => prev.filter((msg) => !msg.isTyping))
        if (response?.data) {
          // Handle special responses

          let displayContent = response.data.text

          if (displayContent.includes("NO_DATA")) {
            displayContent =
              "Câu hỏi của bạn thật sự thú vị. Hiện tại tôi đang thiếu một vài dữ liệu để trả lời chính xác, nhưng đừng lo, tôi sẽ lưu lại và sớm có phản hồi."
          }

          // Check if need to send to BD (similar to React Native version)
          if (response.data.text.includes("BD")) {
            try {
              setTimeout(() => {
                sendToBD({ question: message }).catch((error) => {
                  console.error("Failed to send to BD:", error)
                })
              }, 2000)
            } catch (error) {
              console.error("Error sending to BD:", error)
            }
          }

          // Save AI response to database (async, non-blocking)
          try {
            saveChatMessage(displayContent, "AI").catch((error) => {
              console.error("Failed to save AI message:", error)
            })
          } catch (error) {
            console.error("Error saving AI message:", error)
          }

          const aiMessage: AIMessage = {
            id: `ai-${Date.now()}`,
            content: displayContent,
            isAI: true,
            timestamp: Date.now(),
            createdAt: new Date().toISOString()
          }
          setMessages((prev) => [...prev, aiMessage])
        } else if (typeof response === "string") {
          // Handle fallback string response

          // Save AI response to database
          try {
            saveChatMessage(response, "AI").catch((error) => {
              console.error("Failed to save AI message:", error)
            })
          } catch (error) {
            console.error("Error saving AI message:", error)
          }

          const aiMessage: AIMessage = {
            id: `ai-${Date.now()}`,
            content: response,
            isAI: true,
            timestamp: Date.now(),
            createdAt: new Date().toISOString()
          }
          setMessages((prev) => [...prev, aiMessage])
        } else {
          throw new Error("Invalid response from AI service")
        }
      } catch (err: any) {
        console.error("AI Chat Error:", err)

        // Remove typing indicator
        setMessages((prev) => prev.filter((msg) => !msg.isTyping))

        const errorMessage: AIMessage = {
          id: `error-${Date.now()}`,
          content:
            err?.response?.data?.message ||
            "Xin lỗi, tôi không thể trả lời lúc này. Vui lòng thử lại sau.",
          isAI: true,
          isError: true,
          question: message, // Store original message for retry
          timestamp: Date.now(),
          createdAt: new Date().toISOString()
        }

        setMessages((prev) => [...prev, errorMessage])
        setError(err?.response?.data?.message || "Có lỗi xảy ra khi gửi tin nhắn")
      } finally {
        setIsLoading(false)
      }
    },
    [
      isLoading,
      dailyRequestCount,
      incrementDailyRequests,
      buildConversationContext,
      messages,
      saveChatMessage,
      sendMessageToAI,
      user?.id
    ]
  )

  // Retry failed message
  const retryMessage = useCallback(
    async (originalMessage: string) => {
      await sendMessage(originalMessage, undefined, { isRetry: true })
    },
    [sendMessage]
  )

  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  return {
    messages,
    isLoading,
    error,
    dailyRequestCount,
    dailyLimit: DAILY_REQUEST_LIMIT,
    sendMessage,
    clearMessages,
    setMessages,
    retryMessage
  }
}
